import {MessagingSchema} from 'definitions/Yup';
import * as Yup from 'yup';

import {MediaAttachment, MediaMetadata, MESSAGE_TYPE} from './Common.types';

type UserMetadata = {
  id: string;
  role: string;
  phoneNumber?: string;
};

type UserEntity = {
  conversationId?: string;
  lastActiveAt: number;
  metadata: UserMetadata;
  name: string;
  role: string;
  status: string;
  uid: string;
};

type Entity = {
  entity: UserEntity;
  entityType: 'user';
};

type Entities = {
  receiver: Entity;
  sender: Entity;
};

/**
 * Text Message Data
 */
type TextMessageData = {
  entities: Entities;
  resource: string;
  text: string;
};

/**
 * Media Message Data
 */
type MediaMessageData = {
  attachments: MediaAttachment[];
  entities: Entities;
  metadata: MediaMetadata;
  resource: string;
  url: string;
};

export enum MESSAGE_STATUS {
  SENDING = 'sending',
  SENT = 'sent',
  FAILED = 'failed',
}

type MessageBase = {
  id: string;
  conversationId: string;
  sender: string;
  receiverType: MESSAGE_RECEIVER_TYPE;
  receiver: string;
  category: MESSAGE_CATEGORY;
  sentAt: number;
  updatedAt: number;
  deletedAt?: number;
  deletedBy?: string;
  deliveredAt?: number;
  readAt?: number;
  // Required to handle OPTIMISTIC UI
  tempId?: string;
  tempFile?: File;
  status?: MESSAGE_STATUS;
};

/**
 * Text Message
 */
type TextMessage = MessageBase & {
  type: MESSAGE_TYPE.TEXT;
  data: TextMessageData;
};

/**
 * Media Message
 */
type MediaMessage = MessageBase & {
  type:
    | MESSAGE_TYPE.IMAGE
    | MESSAGE_TYPE.FILE
    | MESSAGE_TYPE.AUDIO
    | MESSAGE_TYPE.VIDEO;
  data: MediaMessageData;
};

/**
 * Message
 */
export type Message = TextMessage | MediaMessage;

/**
 * Message API Response
 */
export type MessageResponse = {
  /**
   * List of messages
   */
  data: Message[];
  /**
   * True if there is a next page
   */
  hasMore: boolean;
  /**
   * message id
   */
  lastMessageId: string;
};

/**
 * Normalized Message Response
 */
export type NormalizedMessageResponse = {
  /**
   *
   */
  entities: {
    messages: {[key: string]: Message};
  };
  /**
   *
   */
  result: string[];
  /**
   * True if there is a next page
   */
  hasMore: boolean;
  /**
   * message id
   */
  lastMessageId: string | null;
};

/**
 *
 */
export type ChatMessage = {
  /**
   *
   */
  content?: string;
  /**
   *
   */
  timestamp: number;
  /**
   *
   */
  user: {[key: string]: string | null};
  /**
   *
   */
  image?: string | null;
};

export enum MESSAGE_RECEIVER_TYPE {
  USER = 'user',
  GROUP = 'group',
}

export enum MESSAGE_CATEGORY {
  MESSAGE = 'message',
  CUSTOM = 'custom',
}

/**
 *
 */
export type MessagingData = Yup.InferType<typeof MessagingSchema>;
/**
 *
 */
export type SendChatData = {
  /**
   *
   */
  receiver: string;
  /**
   *
   */
  receiverType: string;
  /**
   *
   */
  category: string;
  /**
   *
   */
  type: string;
  /**
   *
   */
  data: {
    /**
     *
     */
    text: string;
    /**
     *
     */
    metadata: Record<string, string>;
  };
  /**
   *
   */
  multipleReceivers: {
    /**
     *
     */
    uids: string[];
    /**
     *
     */
    guids: string[];
  };
  /**
   *
   */
  tags: string[];
};

/**
 * Comet Chat Message Types
 */
export type CometEntity = {
  /**
   *
   */
  uid: string;
  /**
   *
   */
  name: string;
  /**
   *
   */
  role: string;
  /**
   *
   */
  avatar: string;
  /**
   *
   */
  status: string;
  /**
   *
   */
  createdAt: number;
  /**
   *
   */
  conversationId?: string;
};

/**
 * Listener Incoming User
 */
type IncomingUserEntity = {
  hasBlockedMe: boolean;
  blockedByMe: boolean;
  deactivatedAt: number;
  uid: string;
  name: string;
  avatar: string;
  lastActiveAt: number;
  role: string;
  status: string;
  metaData?: never;
};

/**
 * Listener Incoming Message
 */
type IncomingMessage = {
  id: string;
  conversationId: string;
  sender: IncomingUserEntity;
  receiverType: MESSAGE_RECEIVER_TYPE;
  receiver: IncomingUserEntity;
  category: MESSAGE_CATEGORY;
  type: MESSAGE_TYPE;
  data: {
    action?: string;
    type: MESSAGE_TYPE;
    text: string;
    attachments?: MediaAttachment[];
    entities: {
      sender: {
        entity: CometEntity;
        entityType: string;
      };
      receiver: {
        entity: CometEntity;
        entityType: string;
      };
    };
    metadata: MediaMetadata;
  };
  sentAt: number;
  updatedAt: number;
  deletedAt?: number;
  deletedBy?: string;
  deliveredAt?: number;
  readAt?: number;
  metadata: MediaMetadata;
};
/**
 *
 */
export type CometChatMessage = {
  /**
   *
   */
  data: IncomingMessage[];
  /**
   *
   */
  meta: Record<string, never>;
};

/**
 *
 */
export type ChatMessageHistory = {[key: string]: ChatMessage};

/**
 *
 */
type MessageStatusUpdate = {
  id: string;
  receiverType: string;
  receiver: {
    uid: string;
  };
  sender: {
    uid: string;
  };
};

/**
 *
 */
export type {
  IncomingMessage,
  IncomingUserEntity,
  MediaMessage,
  MediaMessageData,
  MessageStatusUpdate,
  TextMessage,
  TextMessageData,
};
